import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/shared/ui';
import { useToast } from '@/shared/ui/Toast';
import { useLocation } from '@/shared/hooks/useLocation';
import { useCanSyncIntegrations } from '@/shared/hooks/useCanSyncIntegrations';
import { ReviewSettingsPanel } from '@/modules/reviews/components';
import { IntegrationCard } from '../components/IntegrationCard';
import { GBPCard, GSCCard, SEO_COMING_SOON } from '../components/SeoConnectionCards';
import { SettingsFrame, SettingsGroup, SettingsNote } from '../components/SettingsFrame';
import { useSettingsSections } from '../hooks';
import styles from './SettingsPage.module.css';

const CONNECT_RESULTS: Record<string, { ok: boolean; message: string }> = {
  gsc: { ok: true, message: 'Google Search Console connected successfully' },
  gsc_denied: { ok: false, message: 'Google Search Console authorization was cancelled' },
  gsc_callback_failed: {
    ok: false,
    message: 'Google Search Console connection failed — please try again',
  },
  gbp: { ok: true, message: 'Google Business Profile connected successfully' },
  gbp_denied: { ok: false, message: 'Google Business Profile authorization was cancelled' },
  gbp_callback_failed: {
    ok: false,
    message: 'Google Business Profile connection failed — please try again',
  },
};

/**
 * Settings > Organic & Offline: one sub-section per area under Organic & Offline, and inside Online Visibility one
 * per part of it: SEO / AEO (its connections), Review Accelerator (asking patients for reviews) and Review Responder
 * (replies to Google reviews).
 */
export function OrganicSettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { currentLocation } = useLocation();
  const canSync = useCanSyncIntegrations();
  const sections = useSettingsSections();
  const locationId = currentLocation?.id ?? '';
  const consumedRef = useRef(false);

  // The Search Console and Business Profile connect flows come back here with ?connected= or ?error=.
  useEffect(() => {
    if (consumedRef.current) return;
    const key = searchParams.get('connected') ?? searchParams.get('error');
    const result = key ? CONNECT_RESULTS[key] : undefined;
    if (!result) return;
    consumedRef.current = true;
    if (result.ok) toast.success(result.message);
    else toast.error(result.message);
    const next = new URLSearchParams(searchParams);
    next.delete('error');
    next.delete('connected');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, toast]);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Organic & Offline Settings"
        subtitle="Settings for everything under Organic & Offline."
      />
      <SettingsGroup id="settings-online-visibility" title="Online Visibility">
        <SettingsFrame
          embedded
          level={3}
          id="settings-seo-aeo"
          title="SEO / AEO"
          subtitle="The connections search and AI-visibility work reads."
        >
          <div className={styles.integrationsList}>
            {locationId && <GSCCard locationId={locationId} canSync={canSync} />}
            {locationId && <GBPCard locationId={locationId} canSync={canSync} />}
            {SEO_COMING_SOON.map((c) => (
              <IntegrationCard
                key={c.key}
                displayName={c.displayName}
                description={c.description}
                icon={c.icon}
                status="DISCONNECTED"
                comingSoon
              />
            ))}
          </div>
        </SettingsFrame>
        <SettingsFrame
          embedded
          level={3}
          id="settings-review-accelerator"
          title="Review Accelerator"
          subtitle="How patients are asked for a review."
        >
          <ReviewSettingsPanel part="capture" />
        </SettingsFrame>
        <SettingsFrame
          embedded
          level={3}
          id="settings-review-responder"
          title="Review Responder"
          subtitle="How replies to your Google reviews are drafted."
        >
          <ReviewSettingsPanel part="responder" />
        </SettingsFrame>
      </SettingsGroup>
      {sections.community && (
        <SettingsGroup id="settings-community" title="Community">
          <SettingsNote>No Community settings yet.</SettingsNote>
        </SettingsGroup>
      )}
    </div>
  );
}
